import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { DatePicker, Input, Select } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';

import { useSelector } from 'react-redux';
import GetField from '../Components/GetField';
import styles from '../styles';
import { getGrandTotal } from './common-functions';

const AntSelect = GetField(Select);
const AntInput = GetField(Input);
const { Option } = Select;
const AntDatePicker = GetField(DatePicker);

const ProductAccessoryForm = props => {
  const {
    classes,
    getValues,
    setValue,
    control,
    watch,
    accessoryItem,
    accessoryIndex,
    accessoryAppend,
    accessoryRemove,
  } = props;

  const [accessoriesArr, setAccessoriesArr] = useState([]);

  const jobchartAccessoriesForRpo = useSelector(
    state => state.jobchartAccessoriesForRpo
  );

  useEffect(() => {
    if (jobchartAccessoriesForRpo.productNames.length) {
      const productId = getValues(`accessories[${accessoryIndex}].productId`);
      const productIndex = jobchartAccessoriesForRpo.productIndexes[productId];

      if (productIndex !== undefined) {
        setAccessoriesArr(jobchartAccessoriesForRpo.accessories[productIndex]);
      } else {
        setAccessoriesArr([]);
      }
    }
  }, [
    jobchartAccessoriesForRpo,
    watch(`accessories[${accessoryIndex}].productId`),
  ]);

  useEffect(() => {
    const quantity = getValues(`accessories[${accessoryIndex}].quantity`) || 0;
    const rate = getValues(`accessories[${accessoryIndex}].rate`) || 0;
    const gst = getValues(`accessories[${accessoryIndex}].gst`) || 0;

    const value = +(+quantity * +rate).toFixed(2) || 0;
    const gstValue = +((+value * +gst) / 100).toFixed(2);
    const total = +(+value + +gstValue || 0).toFixed(2);
    const singleTotal = +(total / (quantity || 1)).toFixed(2);

    setValue(`accessories[${accessoryIndex}].value`, value);
    setValue(`accessories[${accessoryIndex}].gstValue`, gstValue);
    setValue(`accessories[${accessoryIndex}].singleTotal`, singleTotal);
    setValue(`accessories[${accessoryIndex}].total`, total);
  }, [
    watch(`accessories[${accessoryIndex}].quantity`),
    watch(`accessories[${accessoryIndex}].rate`),
    watch(`accessories[${accessoryIndex}].gst`),
  ]);

  useEffect(() => {
    const fabricItems = getValues('fabrics');
    const accessoryItems = getValues('accessories');
    const items = getValues('items');
    const stylesItems = getValues('styles');
    setValue(
      'grandTotal',
      getGrandTotal(fabricItems, accessoryItems, items, stylesItems)
    );
  }, [watch(`accessories[${accessoryIndex}].total`)]);

  return (
    <tr>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].productId`}
          control={control}
          defaultValue={accessoryItem.productId}
          render={({ field }) => (
            <AntSelect placeholder="Select product" {...field}>
              {jobchartAccessoriesForRpo.productNames.map(({ product, id }) => (
                <Option key={id} value={id}>
                  {product}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].accessoryId`}
          control={control}
          defaultValue={accessoryItem.accessoryId}
          render={({ field }) => (
            <AntSelect placeholder="Select accessory" {...field}>
              {accessoriesArr.map(accessory => (
                <Option key={accessory.id} value={accessory.id}>
                  {accessory.accessory}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].unit`}
          control={control}
          render={({ field }) => (
            <AntSelect
              disabled
              placeholder="Unit"
              {...{
                ...field,
                value: watch(`accessories[${accessoryIndex}].accessoryId`),
              }}
            >
              {accessoriesArr.map(accessory => (
                <Option key={accessory.id} value={accessory.id}>
                  {accessory.unit}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].jobchartQuantity`}
          control={control}
          render={({ field }) => (
            <AntSelect
              disabled
              placeholder="Jobchart Qty"
              {...{
                ...field,
                value: watch(`accessories[${accessoryIndex}].accessoryId`),
              }}
            >
              {accessoriesArr.map(accessory => (
                <Option key={accessory.id} value={accessory.id}>
                  {accessory.jobchartQuantity}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].quantity`}
          control={control}
          defaultValue={accessoryItem.quantity}
          render={({ field }) => (
            <AntInput
              placeholder="Quantity"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].rate`}
          control={control}
          defaultValue={accessoryItem.rate}
          render={({ field }) => (
            <AntInput
              placeholder="Price"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].value`}
          control={control}
          defaultValue={accessoryItem.value}
          render={({ field }) => (
            <AntInput
              placeholder="Amount"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].gst`}
          control={control}
          defaultValue={accessoryItem.gst}
          render={({ field }) => (
            <AntInput
              placeholder="GST (%)"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].gstValue`}
          control={control}
          defaultValue={accessoryItem.gstValue}
          render={({ field }) => (
            <AntInput
              placeholder="GST Amount"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].singleTotal`}
          control={control}
          defaultValue={accessoryItem.singleTotal}
          render={({ field }) => (
            <AntInput
              placeholder="Total"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].total`}
          control={control}
          defaultValue={accessoryItem.total}
          render={({ field }) => (
            <AntInput
              placeholder="Total"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].hsn`}
          control={control}
          defaultValue={accessoryItem.hsn}
          render={({ field }) => <AntInput placeholder="HSN Code" {...field} />}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`accessories[${accessoryIndex}].deliveryDate`}
          control={control}
          render={({ field }) => (
            <AntDatePicker allowClear placeholder="Date" {...field} />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <DeleteOutlined
          onClick={() => {
            accessoryRemove(accessoryIndex);
          }}
        />
        &nbsp; &nbsp;
        <PlusCircleOutlined onClick={() => accessoryAppend({})} />
      </td>
    </tr>
  );
};

ProductAccessoryForm.propTypes = {
  classes: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
  watch: PropTypes.func.isRequired,
  accessoryRemove: PropTypes.func.isRequired,
  accessoryAppend: PropTypes.func.isRequired,
  accessoryItem: PropTypes.object.isRequired,
  accessoryIndex: PropTypes.number.isRequired,
};

export default withStyles(styles)(ProductAccessoryForm);
